import { advisorV2HttpErrorSnippet } from './advisor-v2-chat-history';
import type { AdvisorV2ChatThreadRow } from './advisor-v2-thread-types';
import { parseSupportSessions, type SupportSessionData } from './support-session';
import {
  advisorV2ThreadDeleteUrl,
  advisorV2ThreadListAllCoreUrl,
  advisorV2ThreadListUrl,
  advisorV2ThreadMessagesUrl,
} from './advisor-v2-thread-urls';

export type AdvisorV2ThreadMessageRow = {
  role: string;
  content: string;
  /** AI SDK stream / persisted assistant id (``msg_<hex>``). */
  message_id?: string | null;
  feedback_rating?: 'up' | 'down' | null;
    feedback_comment?: string | null;
  /** ``human_agent`` rows only: the support agent's display name from DevRev (PS-1357). */
  author_display_name?: string | null;
};

export type AdvisorV2ThreadMessagesResponse = {
  messages: AdvisorV2ThreadMessageRow[];
    pendingReportJobs: number;
  /** Human-support episodes on the thread, oldest first; a live one means HUMAN mode (PS-1358). */
  supportSessions: SupportSessionData[];
};

export type CreateAdvisorV2ThreadApiInput = {
  /** No trailing slash (e.g. from ``resolveAdvisorV2HttpBaseUrl`` plus trailing-slash trim). */
  baseUrl: string;
  useCase: string;
  /**
 * When true, ``listThreads`` calls ``GET /v2/chat/threads`` without ``use_case`` (all org Kin lanes).
 * ``useCase`` is still used for ``fetchMessages`` / ``deleteThread`` unless overridden per call.
 */
  listAllCoreThreads?: boolean;
  fetch: typeof globalThis.fetch;
  buildHeaders: () => Promise<Record<string, string>>;
};

export function createAdvisorV2ThreadApi(input: CreateAdvisorV2ThreadApiInput) {
  const { baseUrl, useCase, listAllCoreThreads = false, fetch: fetchImpl, buildHeaders } = input;
  const root = baseUrl.replace(/\/$/, '');

  return {
    async listThreads(options?: { limit?: number; offset?: number }): Promise<AdvisorV2ChatThreadRow[]> {
      const limit = options?.limit ?? 50;
      const offset = options?.offset ?? 0;
      const url = listAllCoreThreads
        ? advisorV2ThreadListAllCoreUrl(root, limit, offset)
        : advisorV2ThreadListUrl(root, useCase, limit, offset);
      const headers = await buildHeaders();
      const res = await fetchImpl(url, { method: 'GET', headers });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(advisorV2HttpErrorSnippet(text.trim() ? text : `HTTP ${res.status}`));
      }
      const body = (await res.json()) as { threads?: AdvisorV2ChatThreadRow[] };
      return body.threads ?? [];
    },

    async deleteThread(threadId: string, laneOptions?: { useCase?: string }): Promise<void> {
      const lane = laneOptions?.useCase?.trim() || useCase;
      const url = advisorV2ThreadDeleteUrl(root, lane, threadId);
      const headers = await buildHeaders();
      const res = await fetchImpl(url, { method: 'DELETE', headers });
      if (res.status === 204) {
        return;
      }
      const text = await res.text().catch(() => '');
      throw new Error(advisorV2HttpErrorSnippet(text.trim() ? text : `HTTP ${res.status}`));
    },

    async fetchMessages(
      threadId: string,
      options?: { limit?: number; offset?: number; useCase?: string }
    ): Promise<AdvisorV2ThreadMessagesResponse> {
      const limit = options?.limit ?? 200;
      const offset = options?.offset ?? 0;
      const lane = options?.useCase?.trim() || useCase;
      const url = advisorV2ThreadMessagesUrl(root, lane, threadId, limit, offset);
      const headers = await buildHeaders();
      const res = await fetchImpl(url, { method: 'GET', headers });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(advisorV2HttpErrorSnippet(text.trim() ? text : `HTTP ${res.status}`));
      }
      const body = (await res.json()) as {
        messages?: AdvisorV2ThreadMessageRow[];
        pending_report_jobs?: number;
        support_sessions?: unknown;
      };
      const pendingRaw = body.pending_report_jobs;
      const pendingReportJobs =
        typeof pendingRaw === 'number' && Number.isFinite(pendingRaw) ? Math.max(0, Math.trunc(pendingRaw)) : 0;
      return {
        messages: body.messages ?? [],
        pendingReportJobs,
        supportSessions: parseSupportSessions(body.support_sessions),
      };
    },

    async submitFeedback(options: {
      messageId: string;
      rating: 'up' | 'down' | null;
      comment?: string | null;
    }): Promise<void> {
      const q = new URLSearchParams({ use_case: useCase });
      const url = `${root}/v2/chat/feedback?${q.toString()}`;
      const headers = await buildHeaders();
      const body: Record<string, unknown> = {
        message_id: options.messageId,
        rating: options.rating,
      };
      if (options.rating === 'down') {
        body.comment = options.comment ?? null;
      }
      const res = await fetchImpl(url, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 204) {
        return;
      }
      const text = await res.text().catch(() => '');
      throw new Error(advisorV2HttpErrorSnippet(text.trim() ? text : `HTTP ${res.status}`));
    },
  };
}
